/**
 * Single source of truth for camera / walk / panel orientation.
 *
 * Camera sits on a follow sphere at +theta and looks at the player.
 * Wish-forward is the opposite of that offset (into the look target).
 * Babylon is left-handed: looking along wishForward, view-right is
 * LH look × up = (-camOffset.z, +camOffset.x). Using the RH dual
 * (camOffset.z, -camOffset.x) mirrors strafe and the joystick.
 * Panels that face the player (standing on +Z, looking -Z) use yaw π.
 * Never flip a plane with scaling.z = -1 — that inverts the world
 * determinant and mirrors both UVs and WASD.
 */

export const PANEL_FACE_PLAYER_YAW = Math.PI;

export type XZ = { x: number; z: number };

export function lookBasis(theta: number): {
  camOffset: XZ;
  wishForward: XZ;
  wishRight: XZ;
} {
  const camOffset = { x: Math.sin(theta), z: Math.cos(theta) };
  const wishForward = { x: -camOffset.x, z: -camOffset.z };
  const wishRight = { x: -camOffset.z, z: camOffset.x };
  return { camOffset, wishForward, wishRight };
}

export function lookBasisToRef(
  theta: number,
  wishForward: XZ,
  wishRight: XZ,
): void {
  const sin = Math.sin(theta);
  const cos = Math.cos(theta);
  wishForward.x = -sin;
  wishForward.z = -cos;
  wishRight.x = -cos;
  wishRight.z = sin;
}

export function clamp(v: number, a: number, b: number): number {
  return Math.max(a, Math.min(b, v));
}

export function smoothstep(e0: number, e1: number, v: number): number {
  const t = clamp((v - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
}

export function mulberry32(seed: number): () => number {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Rolling low hills around the flat plaza, in world units. */
export function terrainHeight(x: number, z: number): number {
  const px = x / 26;
  const pz = z / 26;
  const macro =
    2.2 * Math.sin(px * 0.45 + 2.1) * Math.cos(pz * 0.5 - 0.8) +
    1.7 * Math.sin(px * 0.72) * Math.sin(pz * 0.58);
  const micro =
    0.75 * Math.sin(px * 1.6 + 1.7) * Math.cos((px - pz) * 1.35) +
    0.45 * Math.sin((px + pz) * 3.1) * Math.cos((px - pz) * 2.2);
  return macro + micro;
}

/** Terrain height at (x,z): hills outside the flat plaza (r < 28). */
export function terrainY(x: number, z: number): number {
  const r = Math.hypot(x, z);
  const flat = r < 28 ? 0 : smoothstep(28, 46, r);
  return terrainHeight(x, z) * flat;
}

export function isMobile(): boolean {
  return (
    window.matchMedia("(max-width: 768px)").matches || window.innerWidth < 768
  );
}

export function hexColor(n: number): string {
  return "#" + n.toString(16).padStart(6, "0");
}

export const QUALITY_KEY = "doorpost.quality";

export type Quality = "low" | "high";

export function readQuality(mobile: boolean, reduce: boolean): Quality {
  const stored = localStorage.getItem(QUALITY_KEY);
  if (stored === "low" || stored === "high") return stored;
  return mobile || reduce ? "low" : "high";
}

export function writeQuality(next: Quality): void {
  localStorage.setItem(QUALITY_KEY, next);
}
